/**
 * Project domain (`project` table): cross-cutting grouping of transactions.
 * Queries and mutations operate on PowerSync's local SQLite — no network.
 */
import {
  and,
  asc,
  count,
  eq,
  isNotNull,
  type PowerSyncDrizzleDB,
  project,
  sql,
  tx,
} from "@cashsync/powersync";
import { liveProjectFilters, liveTxFilters } from "../live/filters";
import { expenseContributionSql, incomeContributionSql } from "./money-sql";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

export interface ProjectRow {
  id: string;
  name: string;
  icon: string | null;
  color: string | null;
  active: boolean;
}

export interface ProjectDraft {
  name: string;
  icon?: string | null;
  color?: string | null;
  active?: boolean;
}

/** Aggregated totals for a project (expense/income in base currency). */
export interface ProjectTotals {
  projectId: string;
  expense: number;
  income: number;
  txCount: number;
}

// ─── Queries ──────────────────────────────────────────────────────────────────

/** Live projects for the user. */
export function getProjectsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(project)
    .where(and(eq(project.user_id, userId), ...liveProjectFilters()))
    .orderBy(asc(project.created_at));
}

export type ProjectWithMeta = Awaited<
  ReturnType<ReturnType<typeof getProjectsQuery>["execute"]>
>[number];

/**
 * Aggregated totals by project in base currency. Same criterion as the period
 * summary: transfers are not expense/income, but their fee is.
 */
export function getProjectTotalsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      projectId: tx.project_id,
      expense: sql<number>`COALESCE(SUM(${expenseContributionSql()}), 0)`.as("expense"),
      income: sql<number>`COALESCE(SUM(${incomeContributionSql()}), 0)`.as("income"),
      txCount: count(tx.id),
    })
    .from(tx)
    .where(and(eq(tx.user_id, userId), ...liveTxFilters(), isNotNull(tx.project_id)))
    .groupBy(tx.project_id);
}

export type ProjectTotalsRow = Awaited<
  ReturnType<ReturnType<typeof getProjectTotalsQuery>["execute"]>
>[number];

/** Project detail by id (live or not — the caller decides). */
export function getProjectDetailQuery(db: PowerSyncDrizzleDB, projectId: string) {
  return db.select().from(project).where(eq(project.id, projectId)).limit(1);
}

/**
 * Indexes aggregated totals by projectId for O(1) lookup in views.
 * Rows with a NULL projectId are ignored (they should not arrive due to isNotNull).
 */
export function indexProjectTotals(
  rows: readonly { projectId: string | null; expense: number; income: number; txCount: number }[],
): Map<string, ProjectTotals> {
  const map = new Map<string, ProjectTotals>();
  for (const row of rows) {
    if (row.projectId) {
      map.set(row.projectId, {
        projectId: row.projectId,
        expense: row.expense,
        income: row.income,
        txCount: row.txCount,
      });
    }
  }
  return map;
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export async function createProjectMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  draft: ProjectDraft,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(project).values({
    id,
    user_id: userId,
    name: draft.name.trim(),
    icon: draft.icon ?? null,
    color: draft.color ?? null,
    active: draft.active === false ? 0 : 1,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateProjectMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    name: string;
    icon: string | null;
    color: string | null;
    active: number;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.name !== undefined) {
    finalUpdates.name = finalUpdates.name.trim();
  }
  await patchRowVerified(db, project, project.id, id, finalUpdates, options);
}

/** Close (active=0) / reopen (active=1) without losing history. */
export async function archiveProjectMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  active: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await updateProjectMutation(db, id, { active: active ? 1 : 0 }, options);
}

/** Soft delete: transactions retain their project_id for history. */
export async function softDeleteProjectMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, project, project.id, id, { deleted_at: nowISO() }, options);
}
